import React, { useEffect, useRef } from "react";
import type { SlashCommandDefinition } from "../shared/mcp-chat-types";
import { COMMAND_CATEGORY_META } from "./mcp-chat-slash-commands";

interface McpChatCommandPaletteProps {
  commands: SlashCommandDefinition[];
  selectedIndex: number;
  onSelect: (cmd: SlashCommandDefinition) => void;
  onHover: (index: number) => void;
}

function getModifierKey(): string {
  return navigator.platform?.toLowerCase().startsWith("mac") ? "\u2318" : "Ctrl+";
}

const McpChatCommandPalette: React.FC<McpChatCommandPaletteProps> = ({
  commands,
  selectedIndex,
  onSelect,
  onHover
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (commands.length === 0) return null;

  const modKey = getModifierKey();

  return (
    <div className="mcp-cmd-palette" ref={listRef} role="listbox" aria-label="Slash commands">
      {commands.map((cmd, idx) => {
        const isSelected = idx === selectedIndex;
        const meta = COMMAND_CATEGORY_META[cmd.category];
        return (
          <button
            key={cmd.name}
            ref={isSelected ? selectedRef : undefined}
            className={`mcp-cmd-palette__item ${isSelected ? "mcp-cmd-palette__item--selected" : ""}`}
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(cmd)}
            onMouseEnter={() => onHover(idx)}
          >
            <span className="mcp-cmd-palette__name">/{cmd.name}</span>
            <span className="mcp-cmd-palette__desc">{cmd.description}</span>
            <span className="mcp-cmd-palette__badge" style={{ "--badge-color": meta.color } as React.CSSProperties}>
              {meta.label}
            </span>
            {cmd.shortcut && (
              <span className="mcp-cmd-palette__shortcut">{cmd.shortcut.replace("Cmd", modKey).replace("+", "")}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default McpChatCommandPalette;
